import { useState, useEffect } from "react";
import { Type, Sun, Moon, MonitorSmartphone, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { applyAppearance } from "@/lib/appearance";

const FONTS = ["Inter", "Manrope", "Plus Jakarta Sans"];
const THEMES = [
  { key: "system", label: "System", icon: MonitorSmartphone },
  { key: "light",  label: "Light",  icon: Sun },
  { key: "dark",   label: "Dark",   icon: Moon },
];

/**
 * AppearancePanel — controls theme (System/Light/Dark) + typography (per-user).
 * Lives in Settings → Profile.
 */
export default function AppearancePanel() {
  const { user, refreshMe } = useAuth();
  const [saving, setSaving] = useState(false);

  const apply = async (patch) => {
    setSaving(true);
    try {
      await api.patch("/user/preferences", patch);
      await refreshMe();
      const nextTheme = patch.appearance_theme || user?.appearance_theme || "dark";
      const nextFont  = patch.appearance_font  || user?.appearance_font  || "Manrope";
      applyAppearance(nextTheme, nextFont);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    applyAppearance(user.appearance_theme || "dark", user.appearance_font || "Manrope");
  }, [user]);

  const theme = user?.appearance_theme || "dark";
  const font  = user?.appearance_font  || "Manrope";

  return (
    <div className="rounded-xl border border-[var(--border-line)] bg-[var(--bg-surface)] p-6" data-testid="appearance-panel">
      <p className="ft-overline mb-1">APPEARANCE</p>
      <h3 className="font-display text-2xl font-bold tracking-tight">Theme &amp; Typography</h3>
      <p className="mt-1 text-sm text-[var(--text-muted)]">
        Choose how RayFuel looks on this account. Saved for you only.
      </p>

      <div className="mt-5">
        <label className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">Theme</label>
        <div className="mt-2 grid grid-cols-3 gap-2">
          {THEMES.map((t) => {
            const Icon = t.icon;
            const active = theme === t.key;
            return (
              <button
                key={t.key}
                data-testid={`theme-${t.key}-btn`}
                onClick={() => apply({ appearance_theme: t.key })}
                disabled={saving}
                className={"px-3 py-3 rounded-lg border flex flex-col items-center gap-1 text-sm transition-colors " +
                  (active
                    ? "border-[#FF5A1F] text-[#FF5A1F] bg-[#FF5A1F]/10"
                    : "border-[var(--border-line)] text-[var(--text-primary)] hover:bg-[var(--hover)]")}
              >
                <Icon size={16} /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-6">
        <label className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">Typography</label>
        <div className="mt-2 grid grid-cols-3 gap-2">
          {FONTS.map((f) => {
            const active = font === f;
            return (
              <button
                key={f}
                data-testid={`font-${f.replace(/\s+/g, '-').toLowerCase()}-btn`}
                onClick={() => apply({ appearance_font: f })}
                disabled={saving}
                style={{ fontFamily: `'${f}', sans-serif` }}
                className={"px-3 py-3 rounded-lg border text-sm transition-colors " +
                  (active
                    ? "border-[#FF5A1F] text-[#FF5A1F] bg-[#FF5A1F]/10"
                    : "border-[var(--border-line)] text-[var(--text-primary)] hover:bg-[var(--hover)]")}
              >
                <span className="inline-flex items-center gap-2">
                  <Type size={14} /> {f}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {saving && (
        <p className="mt-3 text-xs text-[var(--text-muted)] inline-flex items-center gap-1"><Loader2 size={12} className="animate-spin"/> Saving…</p>
      )}
    </div>
  );
}
